import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Autocomplete } from "../../common/components/Autocomplete";

import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitleWithCloseButton from "../../common/components/Dialog/DialogTitleWithCloseButton/DialogTitleWithCloseButton";

import { Typography } from "@mui/material";
import { Box, Button, Icon } from "../../common/components";
import TeamService from "../../service/TeamService";
import { spacing } from "../../styles/theme";
import { FlattenTeam, Team } from "../../types";
import { useThemeBreakpoints } from "../../utils/themeUtils";
import { ROUTES } from "../../constant/common.constant";
import { generatePath, useNavigate } from "react-router-dom";

interface Props {
  open: boolean;
  onClickClose: () => void;
  onAddToTeam: (teamId: string) => void;
  myTeams: Team[];
}

const SearchTeamsModal = (props: Props) => {
  const { open, onClickClose, onAddToTeam, myTeams } = props;
  const { t } = useTranslation();
  const { isMobile } = useThemeBreakpoints();
  const navigate = useNavigate();
  const [selectedTeam, setSelectedTeam] = useState<FlattenTeam | null>(null);

  const onSelection = () => {
    onAddToTeam(selectedTeam?.teamId ?? "");
    onClickClose();
    setSelectedTeam(null);
  };

  const onClose = () => {
    onClickClose();
    setSelectedTeam(null);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth={true}
      fullScreen={isMobile}
      maxWidth={"md"}
    >
      <DialogTitleWithCloseButton
        id="customized-dialog-title"
        onClose={onClose}
        sx={{ mb: 2 }}
      >
        {t("search.search_team")}
      </DialogTitleWithCloseButton>
      <DialogContent>
        <Box
          display="flex"
          flexDirection="column"
          gap={{ xs: spacing.regularMobile, lg: spacing.regularDesktop }}
        >
          <Box>
            <Autocomplete
              id="teamSearchInput"
              value={selectedTeam}
              clearIcon={false}
              onChange={(event, newValue) => {
                setSelectedTeam(newValue);
              }}
              apiCall={TeamService.searchTeams}
              groupBy={(team) =>
                team?.first_name + " " + team?.last_name + " " + team?.email
              }
              getOptionLabel={(team) =>
                team?.name +
                ",  " +
                team?.first_name +
                " " +
                team?.last_name +
                " " +
                team?.email
              }
              getOptionDisabled={
                (team) => myTeams.some((myTeam) => myTeam?.id === team?.teamId) //Disabled if already joined
              }
              renderOption={(props, team) => {
                const isDisabled = myTeams.some(
                  (myTeam) => myTeam?.id === team?.teamId
                );
                return (
                  <Box
                    sx={
                      isDisabled
                        ? {
                            "&:hover": {
                              li: {
                                opacity: `1 !important`,
                                backgroundColor: "action.hover",
                              },
                            },
                          }
                        : undefined
                    }
                  >
                    <li
                      {...props}
                      key={team?.teamId + team?.userId}
                      onClick={() => {
                        if (isDisabled) {
                          navigate(
                            generatePath(ROUTES.TEAM, {
                              teamId: team?.teamId ?? "",
                            })
                          );
                          onClose();
                        } else {
                          setSelectedTeam(team);
                        }
                      }}
                      style={{
                        cursor: "pointer",
                        pointerEvents: "auto",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                      }}
                    >
                      {team?.name}
                      {isDisabled && <Icon icon="pageview" size={20} />}
                    </li>
                  </Box>
                );
              }}
              label={t("general.search")}
            />
          </Box>
          {isMobile && (
            <Button
              onClick={onSelection}
              disabled={selectedTeam === null}
              fullWidth
            >
              <Typography variant="actionBoldBigIcon">
                {t("search.add_to_team")}
              </Typography>
            </Button>
          )}
        </Box>
      </DialogContent>
      {!isMobile && (
        <DialogActions>
          <Button onClick={onSelection} disabled={selectedTeam === null}>
            <Typography variant="actionBoldBigIcon">
              {t("search.add_to_team")}
            </Typography>
          </Button>
        </DialogActions>
      )}
    </Dialog>
  );
};

export default SearchTeamsModal;
